import {
  type ApiHolder,
  type ConfigApi,
  configApiRef,
  type DiscoveryApi,
  discoveryApiRef,
  type FetchApi,
  fetchApiRef,
  microsoftAuthApiRef,
  type OAuthApi,
  useApi,
} from '@backstage/core-plugin-api';
import type { GraphListOptions } from '@ns/plugin-msgraph-proxy-client';

import { entraAssertionSupplier } from './assertion';

/** How a Graph request reaches the proxy: discovery, Backstage's authenticated fetch and the caller's assertion, which is `null` without `microsoftApis.obo.clientId`. */
export type GraphTransport = Pick<
  GraphListOptions,
  'discoveryApi' | 'fetch' | 'getAssertion'
>;

/** A Backstage API that a Graph request needs was not available. */
export class GraphTransportUnavailableError extends Error {
  /**
   * @param apiName - The API that was absent, in words, such as `discovery API`.
   */
  constructor(apiName: string) {
    super(`Graph requests require the ${apiName}.`);
    this.name = 'GraphTransportUnavailableError';
  }
}

function buildGraphTransport(apis: {
  readonly configApi: Pick<ConfigApi, 'getOptionalString'>;
  readonly discoveryApi: DiscoveryApi;
  readonly fetchApi: Pick<FetchApi, 'fetch'>;
  readonly microsoftAuthApi: Pick<OAuthApi, 'getAccessToken'>;
}): GraphTransport {
  return {
    discoveryApi: apis.discoveryApi,
    fetch: apis.fetchApi.fetch,
    getAssertion: entraAssertionSupplier({
      configApi: apis.configApi,
      microsoftAuthApi: apis.microsoftAuthApi,
    }),
  };
}

/**
 * Reads the Graph transport from Backstage's APIs, for code outside React such as a submit validator.
 *
 * @param apiHolder - The APIs Backstage hands a validator in `context.apiHolder`.
 * @returns The transport every Graph request of this plugin needs.
 * @throws {@link GraphTransportUnavailableError} when one of the four APIs is absent.
 */
export function readGraphTransport(apiHolder: ApiHolder): GraphTransport {
  const configApi = apiHolder.get(configApiRef);
  const discoveryApi = apiHolder.get(discoveryApiRef);
  const fetchApi = apiHolder.get(fetchApiRef);
  const microsoftAuthApi = apiHolder.get(microsoftAuthApiRef);
  if (configApi === undefined)
    throw new GraphTransportUnavailableError('configuration API');
  if (discoveryApi === undefined)
    throw new GraphTransportUnavailableError('discovery API');
  if (fetchApi === undefined)
    throw new GraphTransportUnavailableError('fetch API');
  if (microsoftAuthApi === undefined)
    throw new GraphTransportUnavailableError('Microsoft authentication API');
  return buildGraphTransport({
    configApi,
    discoveryApi,
    fetchApi,
    microsoftAuthApi,
  });
}

/**
 * The Graph transport for a component, from Backstage's APIs.
 *
 * @returns The transport every Graph request of this plugin needs.
 */
export function useGraphTransport(): GraphTransport {
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  return buildGraphTransport({
    configApi,
    discoveryApi,
    fetchApi,
    microsoftAuthApi,
  });
}
